import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import clone from 'lodash/clone';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import ApiService from '@/services/apiService';
import WGS84Service from '@/services/wgs84ProjectionService';
import AddressLatLongMixin from '@/components/core/addressLatLongMixin';
export default {
  name: 'CoreAddressPicker',
  mixins: [AddressLatLongMixin],
  props: {
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    addressObject: {
      type: Object,
      required: true
    },
    coordinateLookup: {
      type: Boolean,
      required: false,
      default: true
    },
    callFromN1: {
      type: Boolean,
      required: false,
      default: false
    },
    mapView: {
      type: Boolean,
      required: false,
      default: true
    },
    markerDraggable: {
      type: Boolean,
      required: false,
      default: true
    },
    floatRule: {
      type: Boolean
    }
  },
  data: function () {
    return {
      entries: [],
      isLoading: false,
      search: null,
      address: {},
      coordinates: [null, null],
      // if a manual lat/lng is entered via text-field, do not lookup address any more
      pickedManualCoordinates: false,
      fullScreenMap: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    hasValidCoordinate() {
      return this.areCoordinatesValid(this.coordinates);
    }
  },
  watch: {
    addressObject: {
      handler() {
        this.address = get(this.addressObject, 'address');
        this.coordinates = clone(get(this.addressObject, 'coordinates', this.coordinates));
      },
      deep: true
    },
    coordinates: {
      handler: debounce(function (newCoordinates, oldCoordinates) {
        const isFirstChange = oldCoordinates[0] === null && oldCoordinates[1] === null;
        if (isFirstChange || isEqual(newCoordinates, oldCoordinates)) {
          return;
        }
        if (!this.coordinateLookup || this.pickedManualCoordinates) {
          this.addressSelected();
          return;
        }
        let coordinates = this.coordinates.filter(c => {
          return !isNaN(parseFloat(c));
        });
        if (coordinates.length >= 2) {
          return ApiService.geoCodeCoordinates(coordinates[1], coordinates[0]).then(data => {
            // look up if one of the results matches the given formatted address.
            const matchResult = data.results.find(item => item.formatted_address === this.address.formatted_address);
            if (matchResult) {
              this.entries = [matchResult];
            } else {
              this.entries = [get(data, 'results[0]')];
            }

            // do not (re)publish, if the address has not changed
            // would lead to an endless loop of coordinate-updates and address-lookups
            if (get(this.entries, '[0].formatted_address') !== get(this.address, 'address.formatted_address')) {
              this.address = this.entries[0];
              this.addressSelected();
            }
            return data;
          });
        }
      }, 1000)
    },
    search: debounce(function () {
      // Items have already been loaded
      // if (this.addressSearchResult.length > 0) return

      // Items have already been requested
      if (this.isLoading || this.search === get(this.address, 'formatted_address')) {
        return;
      }
      this.isLoading = true;
      this.remoteLookupAddressCoordinates(this.search);
    }, 500)
  },
  mounted() {
    this.initValuesFromProps();
  },
  methods: {
    initValuesFromProps() {
      this.address = get(this.addressObject, 'address', {});
      this.coordinates = get(this.addressObject, 'coordinates', this.coordinates);
      let formattedAddress = get(this.address, 'formatted_address', '');
      if (this.callFromN1) {
        formattedAddress = formattedAddress || this.address || '';
      }
      this.address = {
        ...this.address,
        formatted_address: formattedAddress,
        geometry: {
          ...get(this.address, 'geometry', {}),
          location: {
            lat: this.coordinates[1],
            lng: this.coordinates[0]
          }
        }
      };
    },
    handleMarkerUpdate(e) {
      this.coordinates = [e.lon, e.lat];
    },
    handleCoordinateChange() {
      this.handleParsingCoordinates(this.coordinates);
      if (this.coordinates[0] === null || this.coordinates[1] === null) {
        return false;
      }
      if (WGS84Service.isValidWgs84({
        lat: this.coordinates[0],
        lon: this.coordinates[1]
      })) {
        this.pickedManualCoordinates = true;
        this.addressSelected();
      }
    },
    handleAddressChange() {
      let newCoordinates = [null, null];
      const addressIsEmptyString = typeof (this.address || '') === 'string' && (this.address || '').trim() === '';
      if (addressIsEmptyString) {
        this.address = {
          formatted_address: ''
        };
      } else if (get(this.address, 'geometry.location.lat')) {
        if (this.coordinateLookup && !this.pickedManualCoordinates) {
          newCoordinates = [this.address.geometry.location.lng, this.address.geometry.location.lat];
        }
      }
      if (this.coordinateLookup && !this.pickedManualCoordinates) {
        this.coordinates = newCoordinates;
      }
      this.addressSelected();
    },
    remoteLookupAddressCoordinates(address) {
      const addressIsEmptyString = typeof (this.address || '') === 'string' && (this.address || '').trim() === '';
      if (addressIsEmptyString) {
        this.isLoading = false;
        return;
      }
      this.isLoading = true;
      ApiService.checkAddress(address).then(data => {
        this.entries = clone(data.results);
      }).catch(error => {
        console.error('Error request address geomapping', error);
      }).finally(() => this.isLoading = false);
    },
    handleReconnectCoordinates() {
      if (get(this.address, 'geometry.location.lat')) {
        this.coordinates = [this.address.geometry.location.lng, this.address.geometry.location.lat];
        this.pickedManualCoordinates = false;
        this.addressSelected(true);
      } else if (get(this.address, 'formatted_address')) {
        ApiService.checkAddress(get(this.address, 'formatted_address')).then(data => {
          if (get(data, 'results[0].geometry.location.lng')) {
            this.coordinates = [get(data, 'results[0].geometry.location.lng'), get(data, 'results[0].geometry.location.lat')];
            this.pickedManualCoordinates = false;
            this.addressSelected(true);
          }
        }).catch(error => {
          console.error('Error request address geomapping', error);
        });
      }
    },
    addressSelected(resetManualLookup = false) {
      const addressData = {
        address: get(this.address, 'formatted_address'),
        coordinates: this.coordinates,
        pickedManualCoordinates: resetManualLookup ? false : !this.coordinateLookup || this.pickedManualCoordinates
      };
      this.$emit('address-selected', addressData);
    },
    clearAddress() {
      this.address = {
        formatted_address: ''
      };
      this.coordinates = [];
      this.$emit('address-clear');
    }
  }
};